// File: typescript/apps/front-end/src/main/react/thoughts/threadCollapseFocus.ts

/**
 * FE#QA-3. Collapsing a thread unmounts the rail the member just activated (the row re-renders as the
 * header-only row) and expanding unmounts the collapsed header, so without a hand-off focus falls to
 * <body> and a keyboard or screen-reader reader restarts from the top of the page. The counterpart is
 * found by the same stable hooks the Playwright specs use; public ids are unique on the page.
 *
 * Shared by the nested-reply tree (`ThoughtReplyTree`) and the opening card's own rail
 * (`ThoughtInDiscussionCardDisplayer`), so the two cannot drift on which control is the counterpart.
 */
export function findThreadCollapseCounterpart(argumentPublicId: string, nowCollapsed: boolean): HTMLElement | null {
    if (typeof document === "undefined") {
        return null;
    }
    const hook = nowCollapsed ? "thought-collapsed-header" : "thought-collapse-rail";
    const candidates = document.querySelectorAll<HTMLElement>(`[data-playwright-id="${hook}"]`);
    for (const el of Array.from(candidates)) {
        if (el.getAttribute("data-thought-public-id") === argumentPublicId) {
            return el;
        }
    }
    return null;
}

/**
 * Focuses the counterpart once the new row has committed. Only a collapse can strand the reader: the rail
 * spans the whole subtree, so it is often activated far below the row that replaces it. `nearest` scrolls
 * only when that row is off screen. On expand the header was just activated in view and the rail starts
 * right under it.
 */
export function focusThreadCollapseCounterpart(argumentPublicId: string, nowCollapsed: boolean): void {
    const target = findThreadCollapseCounterpart(argumentPublicId, nowCollapsed);
    if (!target) {
        return;
    }
    target.focus({ preventScroll: true });
    if (nowCollapsed && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "nearest" });
    }
}
